import React, { forwardRef } from 'react';
import { styled } from '@mui/material';

import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import { Dialog } from 'shell/components/Dialog';
import Icon from 'shell/components/Icon';
import Text from 'shell/components/Text';

const VARIANTS = ['primary', 'destructive'];

const AlertDialog = forwardRef(
    (
        {
            message,
            description,
            note = '',
            open,
            variant = 'primary',
            actionButtonText = 'Action',
            cancelButtonText = 'Cancel',
            onAction,
            onCancel,
            onClose,
            ...rest
        },
        ref
    ) => {
        if (!VARIANTS.includes(variant))
            throw new Error(`Shell: Invalid variant "${variant}" in component AlertDialog.`);

        return (
            <CustomDialog ref={ref} open={open} onClose={onClose} {...rest}>
                <DialogInner>
                    <CustomIcon kind={'error'} variant={variant} />
                    <MessageText component={'h1'} variant={'p2'} $variant={variant}>
                        {message}
                    </MessageText>
                    <DescriptionText component={typeof description === 'string' ? 'p' : 'div'} variant={'p2'}>
                        {description}
                    </DescriptionText>
                    {note && (
                        <NoteText color={'light'} variant={'p3'}>
                            {note}
                        </NoteText>
                    )}
                    <CustomBox>
                        {cancelButtonText && (
                            <Button color={'neutral'} variant={'tertiary'} onClick={onCancel}>
                                {cancelButtonText}
                            </Button>
                        )}
                        <Button
                            data-cy={'alert-dialog-action-button'}
                            color={variant}
                            variant={'secondary'}
                            onClick={onAction}
                        >
                            {actionButtonText}
                        </Button>
                    </CustomBox>
                </DialogInner>
            </CustomDialog>
        );
    }
);

const CustomDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        borderRadius: '12px',
    },
}));

const DialogInner = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '280px',
}));

const CustomIcon = styled(Icon)(({ variant, theme }) => {
    const background = {
        primary: theme.palette.ensightful.primary[500],
        destructive: theme.palette.ensightful.error[500],
    }[variant];

    return {
        borderRadius: '50%',
        marginBottom: theme.spacing(2),
        padding: '0.3125rem',
        fontSize: '1.5rem',
        background,
        color: theme.palette.ensightful.shades[0],
    };
});

const MessageText = styled(Text)(({ $variant, theme }) => {
    const color = {
        primary: theme.palette.ensightful.primary[500],
        destructive: theme.palette.ensightful.error[500],
    }[$variant];

    return {
        textAlign: 'center',
        color,
    };
});

const DescriptionText = styled(Text)(({ theme }) => ({
    marginTop: theme.spacing(2),
    textAlign: 'center',
}));

const NoteText = styled(Text)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const CustomBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(6),
    marginTop: theme.spacing(3),
}));

export default AlertDialog;
